<template>
  <div
    v-if="autorizado"
    class="configuracion"
  >
    <div class="cabecera">
      <h2 class="titulo">
        <v-icon
          color="primary"
          class="mr-1"
        >
          mdi-tune-variant
        </v-icon>
        Configuración
      </h2>
      <p class="bajada">
        Valores que la plataforma usa por dentro. Cambiar uno aquí es cambiarlo
        en todo el sitio, sin volver a desplegar.
      </p>
    </div>

    <div
      v-if="cargando"
      class="estado"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        size="30"
        width="3"
      />
      <span>Cargando la configuración...</span>
    </div>

    <div
      v-else-if="errorCarga"
      class="estado"
    >
      <v-icon
        size="32"
        color="error"
      >
        mdi-alert-circle-outline
      </v-icon>
      <span>{{ errorCarga }}</span>
      <v-btn
        color="primary"
        size="small"
        @click="cargar"
      >
        Reintentar
      </v-btn>
    </div>

    <div
      v-else-if="grupos.length === 0"
      class="estado"
    >
      <v-icon
        size="38"
        color="grey"
      >
        mdi-tune-variant
      </v-icon>
      <span>Todavía no hay ninguna configuración registrada.</span>
    </div>

    <div
      v-for="grupo in grupos"
      v-else
      :key="grupo.nombre"
      class="grupo"
    >
      <div class="grupo-titulo">
        {{ grupo.nombre }}
      </div>

      <v-card
        v-for="fila in grupo.filas"
        :key="fila.key"
        class="fila"
      >
        <div class="fila-texto">
          <div class="fila-nombre">
            {{ fila.name || fila.key }}
          </div>
          <p
            v-if="fila.description"
            class="fila-descripcion"
          >
            {{ fila.description }}
          </p>
          <!-- La clave se muestra pero no se edita: es el nombre por el que el
               backend la busca. Sirve para rastrearla en el codigo. -->
          <code class="fila-clave">{{ fila.key }}</code>
        </div>

        <div class="fila-control">
          <v-switch
            v-if="fila.type === 'booleano'"
            :model-value="borradores[fila.key] === 'true'"
            color="primary"
            density="compact"
            hide-details
            :disabled="!fila.editable || guardando === fila.key"
            :label="borradores[fila.key] === 'true' ? 'Activado' : 'Desactivado'"
            @update:model-value="v => borradores[fila.key] = v ? 'true' : 'false'"
          />
          <v-textarea
            v-else-if="fila.type === 'texto'"
            v-model="borradores[fila.key]"
            class="fg-blue"
            variant="outlined"
            density="compact"
            rows="2"
            auto-grow
            hide-details
            :disabled="!fila.editable || guardando === fila.key"
          />
          <v-text-field
            v-else
            v-model="borradores[fila.key]"
            class="fg-blue"
            variant="outlined"
            density="compact"
            inputmode="decimal"
            :disabled="!fila.editable || guardando === fila.key"
            :hint="pistaDeTipo(fila.type)"
            persistent-hint
          />

          <div class="fila-pie">
            <span class="fila-meta">
              <template v-if="fila.updatedBy">
                {{ fila.updatedBy }} · {{ formatearFecha(fila.updatedAt) }}
              </template>
              <template v-else>
                Sin cambios registrados
              </template>
            </span>

            <v-btn
              v-if="fila.editable"
              color="primary"
              variant="flat"
              size="small"
              :disabled="!haCambiado(fila)"
              :loading="guardando === fila.key"
              @click="guardar(fila)"
            >
              Guardar
            </v-btn>
            <span
              v-else
              class="fila-bloqueada"
            >
              <v-icon size="14">mdi-lock-outline</v-icon>
              No editable
            </span>
          </div>
        </div>
      </v-card>
    </div>

    <v-snackbar
      v-model="aviso"
      :timeout="colorAviso === 'error' ? -1 : 4000"
      :color="colorAviso"
      location="top"
    >
      {{ mensajeAviso }}
      <template #actions>
        <v-btn
          variant="text"
          size="small"
          @click="aviso = false"
        >
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import apisSettings from '@/apis/apisSettings'
import apisUsers from '@/apis/apisUsers'
import { mensajeDeError } from '@/utils/errores'
import { olvidarPublicas } from '@/utils/configuracion'

/**
 * Configuracion de la plataforma, solo para el administrador.
 *
 * No hay crear ni eliminar, ni aqui ni en la API: las filas nacen de las
 * semillas del backend. Una constante existe porque alguien la lee desde el
 * codigo, asi que crearlas desde la pantalla dejaria filas que nadie consulta
 * y borrarlas haria que el lector cayera a su valor por defecto en silencio.
 *
 * Cada fila guarda por su cuenta: son valores sin relacion entre si y un
 * "guardar todo" obligaria a explicar cual de los cambios fue el que fallo.
 */
export default {
  name: 'AdminSettingsView',
  data() {
    return {
      autorizado: false,
      configuraciones: [],
      // key -> valor que se esta editando. Aparte de configuraciones para
      // poder comparar y no habilitar Guardar si no se toco nada.
      borradores: {},
      cargando: true,
      errorCarga: null,
      // key de la fila que se esta guardando, o null. Una sola a la vez,
      // asi el spinner queda en el boton que se pulso y no en todos.
      guardando: null,
      aviso: false,
      mensajeAviso: '',
      colorAviso: 'success'
    }
  },
  computed: {
    /** Las filas ya vienen ordenadas por grupo desde el backend. */
    grupos() {
      const porGrupo = []

      this.configuraciones.forEach(fila => {
        const nombre = fila.group || 'General'
        let grupo = porGrupo.find(g => g.nombre === nombre)

        if (!grupo) {
          grupo = { nombre, filas: [] }
          porGrupo.push(grupo)
        }
        grupo.filas.push(fila)
      })

      return porGrupo
    }
  },
  async created() {
    // Mismo portero que el dashboard de accesos: sin sesion al login, con
    // sesion pero sin rol a la portada. El backend igual responde 401 y 403,
    // esto solo evita que se vea una pantalla vacia.
    const sesionValida = await apisUsers.isValid().catch(() => false)

    if (!sesionValida) {
      this.$router.replace({ name: 'LoginView' })
      return
    }

    const usuario = await apisUsers.GetMe().catch(() => null)

    if (!usuario || usuario.role !== 'administrador') {
      this.$router.replace({ name: 'home' })
      return
    }

    this.autorizado = true
    this.cargar()
  },
  methods: {
    cargar() {
      this.cargando = true
      this.errorCarga = null

      apisSettings.getAll()
        .then(filas => {
          this.configuraciones = filas || []
          this.borradores = {}
          this.configuraciones.forEach(fila => {
            this.borradores[fila.key] = fila.value
          })
        })
        .catch(error => {
          this.errorCarga = mensajeDeError(error, 'No se pudo cargar la configuración')
        })
        .finally(() => {
          this.cargando = false
        })
    },
    haCambiado(fila) {
      const borrador = this.borradores[fila.key]
      return borrador !== undefined && borrador !== fila.value
    },
    pistaDeTipo(tipo) {
      if (tipo === 'entero') {
        return 'Número entero, sin puntos ni comas'
      }
      if (tipo === 'decimal') {
        return 'Número, con punto para los decimales'
      }
      return ''
    },
    guardar(fila) {
      this.guardando = fila.key

      apisSettings.updateValue(fila.key, this.borradores[fila.key])
        .then(actualizada => {
          // Se reemplaza con lo que devolvio el servidor y no con el borrador:
          // asi la fecha y el autor que se ven son los que quedaron guardados.
          const indice = this.configuraciones.findIndex(c => c.key === fila.key)

          if (indice !== -1) {
            this.configuraciones.splice(indice, 1, actualizada)
          }
          this.borradores[fila.key] = actualizada.value

          // El precio y demas valores publicos se piden una sola vez por carga
          // de la aplicacion. Sin esto, el administrador cambia el precio y la
          // portada le sigue mostrando el viejo hasta que recargue.
          olvidarPublicas()

          this.avisar(`${actualizada.name || actualizada.key}: guardado`, 'success')
        })
        .catch(error => {
          this.avisar(mensajeDeError(error, 'No se pudo guardar el valor'), 'error')
        })
        .finally(() => {
          this.guardando = null
        })
    },
    /**
     * La API devuelve LocalDateTime como arreglo ([2026,9,6,18,22,12,...]) y no
     * como ISO, porque el @EnableWebMvc de CorsConfig apaga la
     * autoconfiguracion de Jackson. Se aceptan las dos formas por si algun dia
     * eso se arregla.
     */
    formatearFecha(valor) {
      if (!valor) {
        return ''
      }

      let fecha

      if (Array.isArray(valor)) {
        const [anio, mes, dia, hora = 0, minuto = 0] = valor
        fecha = new Date(anio, mes - 1, dia, hora, minuto)
      } else {
        fecha = new Date(valor)
      }

      if (isNaN(fecha.getTime())) {
        return ''
      }

      return fecha.toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' })
    },
    avisar(mensaje, color) {
      this.mensajeAviso = mensaje
      this.colorAviso = color
      this.aviso = true
    }
  }
}
</script>

<style scoped>
.configuracion {
  max-width: 960px;
  margin: 0 auto;
  padding: 18px 14px 40px;
}

.cabecera {
  margin-bottom: 20px;
}
.titulo {
  display: flex;
  align-items: center;
  font-size: 1.32rem;
  font-weight: 700;
  margin: 0;
}
.bajada {
  margin: 6px 0 0;
  max-width: 560px;
  font-size: 0.86rem;
  line-height: 1.45;
  color: #667085;
}

.grupo {
  margin-bottom: 24px;
}
.grupo-titulo {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #1976d2;
  margin-bottom: 10px;
}

/* Mobile first: el texto arriba y el control debajo. En pantalla grande van
   lado a lado, con el control en un ancho fijo para que los campos de todas
   las filas queden alineados entre si. */
.fila {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  border-radius: 12px;
  margin-bottom: 10px;
}
@media (min-width: 760px) {
  .fila {
    flex-direction: row;
    align-items: flex-start;
    gap: 24px;
  }
  .fila-texto {
    flex: 1 1 auto;
    min-width: 0;
  }
  .fila-control {
    flex: 0 0 300px;
  }
}

.fila-nombre {
  font-size: 0.98rem;
  font-weight: 700;
  color: #1a1a1a;
}
.fila-descripcion {
  margin: 4px 0 0;
  font-size: 0.84rem;
  line-height: 1.45;
  color: #667085;
}
.fila-clave {
  display: inline-block;
  margin-top: 8px;
  padding: 1px 7px;
  border-radius: 5px;
  background: #f2f5f9;
  color: #475467;
  font-size: 0.74rem;
  word-break: break-all;
}

.fila-pie {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 10px;
}
.fila-meta {
  font-size: 0.74rem;
  color: #98a2b3;
  min-width: 0;
}
.fila-bloqueada {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.74rem;
  font-weight: 600;
  color: #98a2b3;
}

.estado {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 56px 12px;
  color: #667085;
  font-size: 0.9rem;
  text-align: center;
}
</style>
